/**
 * Device profile fields on Firestore `users` docs (from GA4 backfill or app callable).
 */

import { formatAppleDeviceModel, isIphoneProModel, isIpadProModel } from "./appleDeviceModelNames";

function pickString(data, ...keys) {
  for (const k of keys) {
    const v = data?.[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/** Primary bucket for analytics charts — iOS, Android, Web, or Unknown. */
export function inferDeviceType(platform, os, category) {
  const p = String(platform || "").trim().toLowerCase();
  const o = String(os || "").trim().toLowerCase();
  const c = String(category || "").trim().toLowerCase();

  if (p === "ios" || o.includes("ios") || o.includes("iphone") || o.includes("ipad")) return "iOS";
  if (p === "android" || o.includes("android")) return "Android";
  if (p === "web" || c === "desktop") return "Web";
  if (c === "mobile" || c === "tablet") {
    if (o.includes("ios") || o.includes("iphone") || o.includes("ipad")) return "iOS";
    if (o.includes("android")) return "Android";
    return "Mobile";
  }
  if (p) {
    if (p === "ios") return "iOS";
    if (p === "android") return "Android";
    if (p === "web") return "Web";
    return p.charAt(0).toUpperCase() + p.slice(1);
  }
  return "Unknown";
}

/** Infer device from Firebase Auth sign-in method when GA4 / app fields are missing. */
export function inferDeviceFromAuthProvider(authProvider) {
  const p = String(authProvider || "").trim().toLowerCase();
  if (!p) return null;

  if (p === "apple") {
    return {
      deviceType: "iOS",
      devicePlatform: "iOS",
      deviceOs: "iOS",
      deviceCategory: "mobile",
      deviceSource: "auth_provider_inferred",
    };
  }

  if (p === "google") {
    return {
      deviceType: "Unknown",
      devicePlatform: "Google sign-in",
      deviceOs: "",
      deviceCategory: "mobile",
      deviceSource: "auth_provider_inferred",
    };
  }

  if (p === "email" || p === "password") {
    return {
      deviceType: "Unknown",
      devicePlatform: "Email sign-in",
      deviceOs: "",
      deviceCategory: "",
      deviceSource: "auth_provider_inferred",
    };
  }

  return {
    deviceType: "Unknown",
    devicePlatform: p.charAt(0).toUpperCase() + p.slice(1),
    deviceSource: "auth_provider_inferred",
  };
}

export function segmentSignInMethod(contact) {
  const p = contact?.auth_provider ?? contact?.authProvider;
  if (!p) return "Not set";
  const key = String(p).trim().toLowerCase();
  const labels = {
    apple: "Apple Sign In",
    google: "Google sign-in",
    email: "Email / password",
    password: "Email / password",
  };
  return labels[key] || String(p);
}

export function formatDeviceSourceLabel(source) {
  if (!source) return "";
  const labels = {
    auth_provider_inferred: "Inferred from sign-in method",
    ga4_backfill: "Google Analytics",
    callable: "App reported",
    client: "App reported",
    app: "Scanbrix app",
  };
  return labels[source] || String(source).replace(/_/g, " ");
}

export const DEVICE_TYPE_LABELS = {
  iOS: "iOS",
  Android: "Android",
  Web: "Web",
  Mobile: "Mobile",
  Unknown: "Unknown",
};

export function formatDeviceTypeLabel(value) {
  if (!value) return "Unknown";
  return DEVICE_TYPE_LABELS[value] || String(value);
}

/** Normalize raw Firestore / GA4 / client payload onto CRM contact fields. */
export function enrichDeviceProfileFields(data) {
  if (!data || typeof data !== "object") return {};
  const devicePlatform = pickString(data, "device_platform", "devicePlatform", "platform");
  const deviceOs = pickString(data, "device_os", "deviceOs", "operating_system", "operatingSystem");
  const deviceCategory = pickString(data, "device_category", "deviceCategory", "deviceCategory");
  const deviceModel = pickString(data, "device_model", "deviceModel", "mobileDeviceModel", "mobile_device_model");
  const deviceBrowser = pickString(data, "device_browser", "deviceBrowser", "browser");
  const deviceTypeRaw = pickString(data, "device_type", "deviceType");
  const deviceSource = pickString(data, "device_source", "deviceSource");
  const deviceUpdatedAt =
    data.device_updated_at ??
    data.deviceUpdatedAt ??
    data.device_updatedAt ??
    data.device_info_updated_at ??
    data.deviceInfoUpdatedAt ??
    null;

  const hasAppDeviceFields = Boolean(
    pickString(data, "platform") ||
      pickString(data, "operating_system", "operatingSystem") ||
      pickString(data, "mobile_device_model", "mobileDeviceModel")
  );

  const hasStored =
    deviceTypeRaw ||
    devicePlatform ||
    deviceOs ||
    deviceModel ||
    deviceBrowser ||
    hasAppDeviceFields;

  if (!hasStored) {
    const authProvider = pickString(data, "auth_provider", "authProvider");
    const inferred = inferDeviceFromAuthProvider(authProvider);
    if (inferred) {
      return {
        deviceType: inferred.deviceType,
        devicePlatform: inferred.devicePlatform || "",
        deviceOs: inferred.deviceOs || "",
        deviceCategory: inferred.deviceCategory || "",
        deviceModel: "",
        deviceBrowser: "",
        deviceSource: inferred.deviceSource,
        deviceUpdatedAt,
      };
    }
  }

  const deviceType =
    deviceTypeRaw || inferDeviceType(devicePlatform, deviceOs, deviceCategory);

  const resolvedSource =
    deviceSource || (hasAppDeviceFields || (devicePlatform && deviceOs) ? "app" : "");

  return {
    deviceType,
    devicePlatform: formatDevicePlatformLabel(devicePlatform),
    deviceOs,
    deviceCategory,
    deviceModel,
    deviceBrowser,
    deviceSource: resolvedSource,
    deviceUpdatedAt,
  };
}

/** Display label for platform (ios → iOS). */
export function formatDevicePlatformLabel(value) {
  if (!value) return "";
  const key = String(value).trim().toLowerCase();
  if (key === "ios") return "iOS";
  if (key === "android") return "Android";
  if (key === "web") return "Web";
  const s = String(value).trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** User-facing device model (iPhone16,1 → iPhone 15 Pro). */
export function formatDeviceModelLabel(raw) {
  return formatAppleDeviceModel(raw);
}

/** Short one-line summary for lists (e.g. "iPhone 15 Pro · iOS 18.5"). */
export function formatDeviceSummary(contact) {
  if (!contact) return "";
  const parts = [];
  const modelLabel = formatDeviceModelLabel(contact.deviceModel);
  if (modelLabel) parts.push(modelLabel);
  else if (contact.deviceType && contact.deviceType !== "Unknown") parts.push(formatDeviceTypeLabel(contact.deviceType));
  if (contact.deviceOs) parts.push(contact.deviceOs);
  else if (contact.deviceBrowser) parts.push(contact.deviceBrowser);
  return parts.join(" · ");
}

export function hasDeviceProfileData(contact) {
  if (!contact) return false;
  return Boolean(
    (contact.deviceType && contact.deviceType !== "Unknown") ||
      contact.devicePlatform ||
      contact.deviceOs ||
      contact.deviceModel ||
      contact.deviceBrowser
  );
}

/** App-reported hardware model (from Firestore `mobile_device_model` / `device_model`). */
export function hasDeviceModelReported(contact) {
  if (!contact) return false;
  return Boolean(String(contact.deviceModel || "").trim());
}

export function isIphoneProContact(contact) {
  if (!hasDeviceModelReported(contact)) return false;
  return isIphoneProModel(contact.deviceModel);
}

export function isIpadProContact(contact) {
  if (!hasDeviceModelReported(contact)) return false;
  return isIpadProModel(contact.deviceModel);
}

/** LiDAR-capable sign-up (iPhone Pro or iPad Pro) — same logic as Device details analytics. */
export function isLidarLeadContact(contact) {
  return isIphoneProContact(contact) || isIpadProContact(contact);
}

/** Segment label for analytics breakdowns. */
export function segmentDeviceType(contact) {
  return formatDeviceTypeLabel(contact?.deviceType);
}

export function segmentDeviceOs(contact) {
  const os = contact?.deviceOs;
  return os ? String(os).trim() : "Not set";
}

export function segmentDeviceCategory(contact) {
  const c = contact?.deviceCategory;
  if (!c) return "Not set";
  const s = String(c).trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function segmentDeviceModel(contact) {
  const m = contact?.deviceModel;
  if (!m) return "Not set";
  return formatDeviceModelLabel(m) || String(m).trim();
}

export function segmentDevicePlatform(contact) {
  const p = contact?.devicePlatform;
  if (!p) return "Not set";
  return formatDevicePlatformLabel(p) || "Not set";
}

function formatDeviceUpdatedAt(value) {
  if (value == null) return "";
  if (typeof value.toDate === "function") return value.toDate().toLocaleString();
  if (typeof value.toMillis === "function") return new Date(value.toMillis()).toLocaleString();
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === "object" && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000).toLocaleString();
  }
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? "" : new Date(t).toLocaleString();
}

/** Rows for contact panel device section. */
export function getContactDeviceDetailRows(contact) {
  if (!contact || !hasDeviceProfileData(contact)) return [];
  const rows = [];
  const push = (label, value) => {
    const v = value != null ? String(value).trim() : "";
    if (!v) return;
    rows.push({ label, value: v });
  };

  push("Device type", formatDeviceTypeLabel(contact.deviceType));
  push("Platform", contact.devicePlatform);
  push("Operating system", contact.deviceOs);
  push("Category", contact.deviceCategory);
  if (contact.deviceModel) {
    const friendly = formatDeviceModelLabel(contact.deviceModel);
    push(
      "Model",
      friendly && friendly !== contact.deviceModel
        ? `${friendly} (${contact.deviceModel})`
        : friendly || contact.deviceModel
    );
  }
  push("Browser", contact.deviceBrowser);
  if (contact.deviceSource) push("Source", formatDeviceSourceLabel(contact.deviceSource));
  const updated = formatDeviceUpdatedAt(contact.deviceUpdatedAt);
  if (updated) push("Last updated", updated);

  return rows;
}
